import { apiService } from "@/lib/api/service";
import type {
  CreatedInvite,
  CurrentOrganization,
  DiscoverySetupPayload,
  KnowledgeEntry,
  OrganizationMember,
  OrgBilling,
  OrgBranding,
  OrgRole,
  PendingInvite,
  UserOrganization,
  WorkspaceBlueprint,
} from "../types";

export const organizationApi = {
  getCurrent: () =>
    apiService.get<CurrentOrganization>("/organizations/me", { silent: true }),

  list: () =>
    apiService.get<{ organizations: UserOrganization[] }>("/organizations", {
      silent: true,
    }),

  members: () =>
    apiService.get<{ members: OrganizationMember[]; count: number }>(
      "/organizations/members",
      { silent: true },
    ),

  createTeam: (name: string) =>
    apiService.post<{ organization_id: string }>(
      "/organizations",
      { name },
      { successMessage: "Team created" },
    ),

  // Public sign-up: provision a Discovery workspace owned by the new user.
  // Idempotent — no-ops if they already have a workspace or a pending invite.
  provisionSelfServe: () =>
    apiService.post<{
      organization_id: string | null;
      created: boolean;
      has_invite?: boolean;
    }>("/organizations/self-serve", {}, { silent: true }),

  // AI-draft the workspace blueprint from a company name (setup wizard).
  draftDiscovery: (companyName: string, notes = "") =>
    apiService.post<WorkspaceBlueprint>(
      "/organizations/discovery/draft",
      { company_name: companyName, notes },
      { silent: true },
    ),

  // Finalize the self-serve setup wizard (branding, knowledge, template).
  completeDiscoverySetup: (payload: DiscoverySetupPayload) =>
    apiService.patch<{ setup_completed: boolean }>(
      "/organizations/discovery/setup",
      payload,
      { silent: true },
    ),

  switchOrg: (organizationId: string) =>
    apiService.post(`/organizations/switch/${organizationId}`),

  invite: (email: string, role: OrgRole) =>
    apiService.post<CreatedInvite>(
      "/organizations/invites",
      { email, role },
      { successMessage: "Invite created" },
    ),

  listInvites: () =>
    apiService.get<{ invites: PendingInvite[] }>("/organizations/invites", {
      silent: true,
    }),

  revokeInvite: (token: string) =>
    apiService.delete(`/organizations/invites/${token}`, {
      successMessage: "Invite revoked",
    }),

  updateRole: (userId: string, role: OrgRole) =>
    apiService.patch(`/organizations/members/${userId}`, { role }),

  removeMember: (userId: string) =>
    apiService.delete(`/organizations/members/${userId}`, {
      successMessage: "Member removed",
    }),

  listKnowledge: () =>
    apiService.get<{ entries: KnowledgeEntry[]; count: number }>(
      "/organizations/knowledge",
      { silent: true },
    ),

  addKnowledge: (content: string) =>
    apiService.post<KnowledgeEntry>(
      "/organizations/knowledge",
      { content },
      { successMessage: "Added to company knowledge" },
    ),

  removeKnowledge: (memoryId: string) =>
    apiService.delete(`/organizations/knowledge/${memoryId}`, {
      successMessage: "Removed",
    }),

  getBrainKnowledge: () =>
    apiService.get<{ brain_knowledge: string }>(
      "/organizations/brain-knowledge",
      { silent: true },
    ),

  updateBrainKnowledge: (brainKnowledge: string) =>
    apiService.patch<{ brain_knowledge: string }>(
      "/organizations/brain-knowledge",
      { brain_knowledge: brainKnowledge },
      { successMessage: "Workspace knowledge saved" },
    ),

  updateWorkspaceDetails: (payload: { name?: string; description?: string }) =>
    apiService.patch<{ name: string; description: string | null }>(
      "/organizations/workspace-details",
      payload,
      { successMessage: "Workspace updated" },
    ),

  updateBranding: (branding: OrgBranding) =>
    apiService.patch<OrgBranding>("/organizations/branding", branding, {
      successMessage: "Branding updated",
    }),

  getJoinLink: () =>
    apiService.get<{ enabled: boolean; url: string | null }>(
      "/organizations/join-link",
      { silent: true },
    ),

  enableJoinLink: (rotate = false) =>
    apiService.post<{ enabled: boolean; url: string | null }>(
      `/organizations/join-link/enable${rotate ? "?rotate=true" : ""}`,
      {},
      { successMessage: rotate ? "New link generated" : "Invite link enabled" },
    ),

  disableJoinLink: () =>
    apiService.post<{ enabled: boolean; url: string | null }>(
      "/organizations/join-link/disable",
      {},
      { successMessage: "Invite link disabled" },
    ),

  previewJoin: (token: string) =>
    apiService.get<{ organization_id: string; organization_name: string }>(
      `/organizations/join/${token}`,
      { silent: true },
    ),

  acceptJoin: (token: string) =>
    apiService.post<{ organization_id: string }>(
      `/organizations/join/${token}`,
    ),

  getBilling: () =>
    apiService.get<OrgBilling>("/organizations/billing", { silent: true }),

  setPlan: (plan: string) =>
    apiService.post<OrgBilling>(
      "/organizations/plan",
      { plan },
      { successMessage: "Plan updated" },
    ),
};
